import { HttpPromise } from "@shared/svelte/Modules/ApiService";
import { shouldNotTrack } from "@shared/services/internal_user_check_helpers";

import type {
  CarrotSponsoredProductAdEntity,
  CarrotBannerAdEntity,
  CarrotShoppableUnitAdEntity,
  CarrotShoppableUnitProductEntity,
} from "@shared/services/event_tracking/entities";

export enum CarrotTypes {
  Banner = "carrot_banner_ad",
  ShoppableUnit = "carrot_shoppable_unit_ad",
  ShoppableUnitLogo = "carrot_shoppable_unit_logo",
  ShoppableUnitProduct = "carrot_shoppable_unit_product",
  SponsoredProduct = "carrot_sponsored_product_ad",
}

export enum CarrotEvents {
  AddedToCart = "addedToCart",
  Click = "click",
  ViewableInViewport = "viewableInViewport",
  OnePxInViewport = "onePxInViewport",
  BeginToRender = "beginToRender",
}

const carrotSponsoredProductEvents = {
  addedToCart: "ian.sponsored_product.added_to_cart",
  click: "ian.sponsored_product.clicked",
  beginToRender: "ian.sponsored_product.begin_to_render_viewport",
  viewableInViewport: "ian.sponsored_product.viewable_in_viewport",
  onePxInViewport: "ian.sponsored_product.1px_in_viewport",
};

const carrotDisplayEvents = {
  click: "ian.display.click_creative",
  onePxInViewport: "ian.display.viewport_1px_creative",
  viewableInViewport: [
    "ian.display.viewport_viewable_creative_asset",
    "ian.display.viewport_viewable_creative",
  ],
  beginToRender: [
    "ian.display.viewport_load_creative",
    "ian.display.viewport_viewable_begin_to_render_creative",
    "ian.display.viewport_viewable_begin_to_render_creative_asset",
  ],
};

const carrotDisplayProductEvents = {
  click: "ian.display.click_creative_item",
  onePxInViewport: "ian.display.viewport_1px_creative_item",
  viewableInViewport: "ian.display.viewport_viewable_creative_item",
};

const sendCarrotEvent = async (
  event: string,
  carrotEventData,
  indexPosition: number | null = null,
) => {
  // new Date() appends a human-readable timezone, which can be too long for rails
  // en-GB locale formats the timestamp in a uniform way that rails can parse
  const sentAt = new Date().toLocaleString("en-GB", { timeZoneName: "short" });

  const data = {
    event,
    display_position: indexPosition,
    forward_url: carrotEventData.forward_url,
    interaction_id: carrotEventData.click_interaction_id,
    object_tracking_id: carrotEventData.object_tracking_id,
    creative_type: carrotEventData.creative_type,
    asset_type: carrotEventData.asset_type,
    page_view_id: carrotEventData.page_view_id,
    product_line_id: carrotEventData.product_line_id,
    sent_at: sentAt,
    format_type: carrotEventData.ad_format,
  };

  const options = {
    method: "GET",
    url: "api/retailer/carrot_ads/track_carrot_event",
    data: data,
  };

  try {
    await HttpPromise(options);
  } catch (err) {
    console.error(err);
  }
};

const extractCarrotEntity = (entities: any) => {
  const carrotEntities = entities.filter(e => e.type.startsWith("carrot"));

  const shoppableUnitProductEntity = carrotEntities.find(
    e => e.type === "carrot_shoppable_unit_product",
  );

  if (shoppableUnitProductEntity) {
    return shoppableUnitProductEntity;
  } else {
    return carrotEntities[0];
  }
};

export const trackCarrotEvent = (event: string, entities: any) => {
  if (shouldNotTrack()) {
    return;
  }

  const carrotAdEntity = extractCarrotEntity(entities);

  if (!carrotAdEntity) {
    return;
  }
  const adType = carrotAdEntity.type;

  switch (adType) {
    case CarrotTypes.Banner:
      trackDisplayAd(event, carrotAdEntity);
      break;
    case CarrotTypes.ShoppableUnit:
      trackDisplayAd(event, carrotAdEntity);
      break;
    case CarrotTypes.ShoppableUnitProduct:
      trackShoppableUnitProduct(event, entities, carrotAdEntity);
      break;
    case CarrotTypes.SponsoredProduct:
      trackSponsoredProductAd(event, entities, carrotAdEntity);
      break;
  }
};

const shoppableUnitEntity = (entities: any) => {
  return entities.find(e => e.type === "carrot_shoppable_unit_ad");
};

const trackDisplayAd = (
  event: string,
  carrotAdEntity: CarrotBannerAdEntity | CarrotShoppableUnitAdEntity,
) => {
  const carrotEvent = carrotDisplayEvents[event];
  if (Array.isArray(carrotEvent)) {
    carrotEvent.forEach(e => {
      sendCarrotEvent(e, carrotAdEntity);
    });
  } else {
    sendCarrotEvent(carrotEvent, carrotAdEntity);
  }
};

const trackShoppableUnitProduct = (
  event: string,
  entities: any,
  shoppableUnitProductEntity: CarrotShoppableUnitProductEntity,
) => {
  const carrotEvent = carrotDisplayProductEvents[event];

  if (!carrotEvent) {
    return;
  }

  const shoppableUnitData = shoppableUnitEntity(entities);

  const carrotEventData = {
    ...shoppableUnitData,
    ...shoppableUnitProductEntity,
    ad_format: "shoppable_unit_product",
  };

  sendCarrotEvent(carrotEvent, carrotEventData);
};

const trackSponsoredProductAd = (
  event: string,
  entities: any,
  sponsoredProductEntity: CarrotSponsoredProductAdEntity,
) => {
  const carrotEvent = carrotSponsoredProductEvents[event];

  // Carrot does not dedupe impression/view events so we only want to send send the first one
  // which is fired on the product card and ignore any others that are fired on the sku item
  const skuItemEntity = entities.find(e => e.type === "product_card_sku_item");
  const carrotClickEvents = [
    carrotSponsoredProductEvents.click,
    carrotSponsoredProductEvents.addedToCart,
  ];
  if (skuItemEntity && !carrotClickEvents.includes(carrotEvent)) {
    return;
  }

  const productCardEntity = entities.find(e => e.type === "product_card");
  // indexPosition is 0-indexed, but Carrot wants us to send 1-indexed
  const indexPosition = productCardEntity.indexPosition + 1;
  sendCarrotEvent(carrotEvent, sponsoredProductEntity, indexPosition);
};
